import React from 'react';
import type { ComponentProps } from 'react';
import { FastField, Field } from 'formik';
import type { FieldProps, FieldValidator } from 'formik';
import { ODSInput } from 'bc-ods';
import { getValidationProps } from 'utils/formik-utils';

type PropsType = Omit<ComponentProps<typeof ODSInput>, 'children'> & {
  name: string;
  eagerValidation?: boolean;
  fast?: boolean;
  validate?: FieldValidator;
  as?: React.ComponentType<ComponentProps<typeof ODSInput>> | string;
  component?: React.ComponentType<React.PropsWithChildren<FieldProps>> | string;
  children?: ((props: FieldProps) => React.ReactNode) | React.ReactNode;
};

const ValidatedFormikField = React.forwardRef<HTMLInputElement, PropsType>(
  function ValidatedFormikField(props, ref) {
    const {
      as: As = ODSInput,
      component: Component,
      className,
      children,
      eagerValidation,
      fast,
      name,
      validate,
      ...fieldProps
    } = props;
    const Tag = fast ? FastField : Field;

    return (
      <Tag name={name} validate={validate}>
        {({ field, form, meta }) => {
          const { error, touched } = meta;
          const mergedProps = {
            ...fieldProps,
            ...getValidationProps(error, eagerValidation || touched, className),
            ref,
          };

          if (children && typeof children === 'function') {
            return children({
              field: { ...field, ...mergedProps },
              meta: {
                ...meta,
                validationProps: ({ className: cn = '', ...rest } = {}) => ({
                  ...rest,
                  ...getValidationProps(error, eagerValidation || touched, cn),
                }),
              },
              form,
            });
          }
          if (Component) {
            return (
              <Component field={field} form={form} meta={meta} {...mergedProps}>
                {children}
              </Component>
            );
          }
          return (
            <As {...field} value={field.value ?? ''} {...mergedProps}>
              {children}
            </As>
          );
        }}
      </Tag>
    );
  }
);

export default ValidatedFormikField;
